import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import Drawer from "react-modern-drawer";
import claimValidate from "../../assets/claims-guide/claim-validate.png";
import claimPrice from "../../assets/claims-guide/claim-price.png";
import claimBenefits from "../../assets/claims-guide/claim-benefits.png";
import visitDefinition from "../../assets/provider-guide/visit-definition.png";
import visitBenefitNetwork from "../../assets/provider-guide/visit-benefit-network.png";
import visitSupplier from "../../assets/provider-guide/visit-supplier.png";
import moneyNotYet from "../../assets/claims-guide/money-not-yet.png";
import moneyUnits from "../../assets/claims-guide/money-units.png";
import moneyDollar from "../../assets/claims-guide/money-dollar.png";
import moneyAccums from "../../assets/claims-guide/money-accums.png";
import claimsFunding from "../../assets/finance-guide/claims-funding.png";
import paymentOut from "../../assets/finance-guide/payment-out.png";
import homeStopLoss from "../../assets/home-guide/home-stoploss.png";
import "../provider/ProviderGuide.scss";
import "./ClaimsGuide.scss";

const PAY = [
    {
        to: "/claims",
        img: moneyAccums,
        alt: "A pencil writing the owed amount into a ledger",
        kicker: "Payable",
        when: "Final or Denied writes what HRP owes. $0 still gets a payable.",
        name: "Claim payable",
    },
    {
        to: "/finance/payment-cycles",
        img: moneyUnits,
        alt: "A calendar for the payment schedule",
        kicker: "Cycle",
        when: "The plan names the supplier or member claims schedule.",
        name: "Payment cycle",
    },
    {
        to: "/finance/payment-cycles",
        img: paymentOut,
        alt: "A document becoming a coin",
        kicker: "Run",
        when: "Release date is today or earlier. Grouped by payee and bank.",
        name: "Payment run picks it up",
    },
    {
        to: "/finance/funding-requests",
        img: claimsFunding,
        alt: "A claim clipboard beside a money bag",
        kicker: "Funding",
        when: "A self-funded run creates the employer cash call. No separate create step.",
        name: "Funding request",
    },
    {
        to: "/finance/guide",
        img: moneyDollar,
        alt: "A stack of bills under a cap",
        kicker: "Pay",
        when: "After the cash is in. Funding is not the provider check.",
        name: "Check or EFT to the payee",
    },
];

const ADJUDICATION = [
    { n: 1, name: "Submit", detail: "837 or keyed in Manager." },
    { n: 2, name: "Match", detail: "Member, practitioner, and supplier." },
    { n: 3, name: "Validate", detail: "Complete and valid, or Repair / Reject. No dollars yet." },
    { n: 4, name: "External editor", detail: "Optional. CES or ClaimsXten after repair edits pass." },
    { n: 5, name: "DRG", detail: "Inpatient facility only. An office visit skips this." },
    { n: 6, name: "Covered?", detail: "Was this member eligible that day, on this plan." },
    { n: 7, name: "Supplier contract", detail: "Find the deal. No match can go to Repricing." },
    { n: 8, name: "Contract exclusions", detail: "Deny if the contract says no." },
    { n: 9, name: "Price", detail: "Allowed amount from the fee table or a pricer." },
    { n: 10, name: "Coverage tier", detail: "In-network or out-of-network." },
    { n: 11, name: "Benefit exclusions", detail: "Deny if the plan does not cover it." },
    { n: 12, name: "Unit limits", detail: "Visit and day caps. Over the bound → deny the line." },
    { n: 13, name: "Auth / UM", detail: "Referral or pre-cert. Missing → plan or contract penalty." },
    { n: 14, name: "Benefit", detail: "Copay and coinsurance for this service." },
    { n: 15, name: "Deductible", detail: "Member share toward the individual or family deductible on this tier." },
    { n: 16, name: "OOP max", detail: "If out-of-pocket is already met, the plan pays the rest." },
    { n: 17, name: "Dollar limits", detail: "Annual $ caps. Over → deny the line." },
    { n: 18, name: "Duplicates", detail: "Same service already paid → deny." },
    { n: 19, name: "Secondary editor", detail: "Optional. Runs after the money math." },
    { n: 20, name: "Final claim", detail: "Write the result, the payable, and the accums." },
];

const PHASES = [
    {
        id: "intake",
        range: "01–05",
        name: "Intake",
        lead: "Name the people. Stop if the form is bad. No dollars yet.",
        from: 1,
        to: 5,
    },
    {
        id: "coverage",
        range: "06",
        name: "Coverage",
        lead: "Was this member eligible that day.",
        from: 6,
        to: 6,
    },
    {
        id: "price",
        range: "07–11",
        name: "Price",
        lead: "Find the contract and the tier. Overlap of network codes is not a human pick.",
        from: 7,
        to: 11,
        more: { to: "/claims/guide#network", label: "How the contract is picked" },
    },
    {
        id: "money",
        range: "12–17",
        name: "Benefits math",
        lead: "Only after the line is priced and the tier is known. Individual vs family, and IN vs OON, are separate buckets.",
        from: 12,
        to: 17,
        gate: true,
    },
    {
        id: "finish",
        range: "18–20",
        name: "Final claim",
        lead: "Write the result and the payable. Accums land here.",
        from: 18,
        to: 20,
    },
];

const STOPLOSS = [
    {
        name: "One person",
        detail: "That member’s paid claims pass a dollar line. The reinsurer shares the excess.",
    },
    {
        name: "The whole group",
        detail: "Everyone’s paid claims pass a dollar line. The reinsurer shares the group excess.",
    },
];

const TYPES = [
    {
        name: "Professional",
        form: "CMS-1500 · 837P",
        when: "Office, clinician, outpatient.",
        detail: "Each line has its own service dates. This is most of what you see on /claims.",
    },
    {
        name: "Institutional",
        form: "UB-04 · 837I",
        when: "Hospital or facility.",
        detail: "Statement from / through dates. Inpatient also gets a DRG before pricing.",
    },
    {
        name: "Dental",
        form: "Dental form",
        when: "Dental treatment.",
        detail: "Predetermination estimates only — no payment is produced.",
    },
    {
        name: "Converted",
        form: "Brought in from another payer",
        when: "History for accums.",
        detail: "Feeds deductible and limits. Cannot be re-adjudicated until it is renewed.",
        opens: "converted",
    },
];

const GUIDE_DRAWER_SIZE = 440;

const STATUSES = [
    { name: "Final", when: "Adjudicated. Accums written. Ready for the payment run." },
    { name: "Denied", when: "Adjudicated, $0 by a provision. Accums can still move." },
    { name: "Needs Review", when: "Data is ok. A person must finish it." },
    { name: "Needs Repair", when: "Incomplete. No financials, accums unchanged." },
    { name: "Rejected", when: "Failed minimum rules. Cannot be edited." },
    { name: "Needs Repricing", when: "No contract match. Sent to a repricer." },
];

const NETWORK = [
    {
        to: "/providers/suppliers",
        img: visitSupplier,
        alt: "A clinic building for the matched supplier",
        kicker: "Provider side",
        when: "TIN matches a supplier. Collect benefit networks on the supplier, location, practitioner, and supplier networks — not only the IPA roster.",
        name: "Provider network codes",
    },
    {
        to: "/config/network-definitions",
        img: visitDefinition,
        alt: "A clipped list of the plan’s in-network buckets",
        kicker: "Plan side",
        when: "The plan’s network definition lists the benefit networks it can use.",
        name: "Plan network codes",
    },
    {
        to: "/providers/networks?type=benefit",
        img: visitBenefitNetwork,
        alt: "A shield for a benefit network",
        kicker: "Overlap",
        when: "Keep codes on both lists. No overlap → out of network and non-par pricing.",
        name: "Shared benefit networks",
    },
    {
        to: "/config/network-definitions",
        img: claimBenefits,
        alt: "An open plan booklet naming the in-network tier",
        kicker: "First match",
        when: "Several in-network tiers can match. HRP takes the first one on the definition.",
        name: "Nobody picks a network",
    },
    {
        to: "/providers/contracts",
        img: claimPrice,
        alt: "A contract and fee table with a dollar sign",
        kicker: "Contract",
        when: "Direct TIN contract before a network contract. Then the fee schedule for that deal and benefit network.",
        name: "Specificity, then the rate",
    },
];

const ClaimsGuide = () => {
    const [convertedOpen, setConvertedOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const id = location.hash.replace(/^#/, "");
        if (!id) return;
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [location.hash]);

    return (
        <div className="container claims-guide">
            <div className="provider-guide">
            <div className="provider-guide__frame">
                <header className="provider-guide__intro">
                    <p className="provider-guide__eyebrow">Claims</p>
                    <h1>How HRP adjudicates a claim</h1>
                    <p>
                        Twenty steps, then how the contract is picked, then the payment
                        run. Stop-loss is after the payable.{" "}
                        <Link to="/claims">Back to claims</Link>
                    </p>
                </header>

                <section id="steps" className="claims-guide__section">
                    <header>
                        <p className="provider-guide__eyebrow">One claim</p>
                        <h2>How HRP adjudicates</h2>
                        <p>
                            Money starts at step 12. Stop-loss is after step 20, once a
                            payable exists.
                        </p>
                    </header>
                    {PHASES.map((phase) => (
                        <div key={phase.id} id={phase.id} className="claims-guide__phase">
                            <header>
                                <span>{phase.range}</span>
                                <h3>{phase.name}</h3>
                                <p>{phase.lead}</p>
                            </header>
                            {phase.gate ? (
                                <div className="provider-guide__card claims-guide__gate">
                                    <img src={moneyNotYet} alt="A claim form locked before any dollars are counted" />
                                    <span>Not yet</span>
                                    <em>Needs Repair or Rejected. Deductible, OOP, and accums stay as they were.</em>
                                    <strong>No financials</strong>
                                </div>
                            ) : null}
                            <ol className="claims-guide__steps">
                                {ADJUDICATION.filter((step) => step.n >= phase.from && step.n <= phase.to).map((step) => (
                                    <li key={step.n}>
                                        <span>{String(step.n).padStart(2, "0")}</span>
                                        <strong>{step.name}</strong>
                                        <em>{step.detail}</em>
                                    </li>
                                ))}
                            </ol>
                            {phase.more ? (
                                <p className="claims-guide__hint">
                                    <Link to={phase.more.to}>{phase.more.label}</Link>
                                </p>
                            ) : null}
                            {phase.id === "money" ? (
                                <p className="claims-guide__hint">
                                    A later claim with an earlier date of service can change
                                    the deductible on a claim you already finalized — that is
                                    why reprocess exists. A $0 replacement of the same claim
                                    number drops it off the member’s accumulator list.
                                </p>
                            ) : null}
                        </div>
                    ))}
                    <ul className="claims-guide__statuses">
                        {STATUSES.map((s) => (
                            <li key={s.name}>
                                <Link to="/claims">
                                    <strong>{s.name}</strong>
                                    <em>{s.when}</em>
                                </Link>
                            </li>
                        ))}
                    </ul>
                    <p className="claims-guide__hint">
                        Complex claims (confinement, COB) can loop this list more than
                        once — N-pass. Same steps, extra trips.{" "}
                        <Link to="/claims/guide#stoploss">Stop-loss after Final</Link>
                    </p>
                    <p className="claims-guide__refs">
                        HealthRules Manager User Guides, Vol. 8: Claims (23.3) — “The
                        Claim Adjudication Process” (steps 1–20), “Multiple-Pass
                        (N-Pass) Adjudication,” and “Claim Status Assignment During
                        Adjudication.” Finalization / Benefits phase in the same
                        chapter.{" "}
                        <Link to="/hrp-docs">Open HRP docs</Link>
                    </p>
                </section>

                <section id="network" className="claims-guide__section">
                    <header>
                        <p className="provider-guide__eyebrow">Price</p>
                        <h2>How the contract is picked</h2>
                        <p>
                            “Network codes” are benefit networks. They have to appear on
                            both the provider and the plan. Multiple matches do not stop
                            the claim for a person.
                        </p>
                    </header>
                    <ol className="claims-guide__path claims-guide__network">
                        {NETWORK.map((step) => (
                            <li key={step.kicker}>
                                <Link to={step.to} className="provider-guide__card">
                                    <img src={step.img} alt={step.alt} />
                                    <span>{step.kicker}</span>
                                    <em>{step.when}</em>
                                    <strong>{step.name}</strong>
                                </Link>
                            </li>
                        ))}
                    </ol>
                    <div className="provider-guide__card claims-guide__gate">
                        <img src={claimValidate} alt="A checklist that is not a stop for overlapping networks" />
                        <span>Not this</span>
                        <em>
                            If more than one network code overlaps, HRP does not wait for
                            someone to choose. It keeps the first matching in-network tier
                            on the definition.
                        </em>
                        <strong>No human pick on overlap</strong>
                    </div>
                    <p className="claims-guide__hint">
                        A remaining contract tie uses the HCC tie-breaker (later vs earlier
                        start, then last edited). A person only sees the claim if two fee
                        details are equally specific — Review and Repair — or no contract
                        is found — Needs Repricing.{" "}
                        <Link to="/providers/guide">How provider records nest</Link>
                    </p>
                </section>

                <section id="pay" className="claims-guide__section">
                    <header>
                        <p className="provider-guide__eyebrow">After Final</p>
                        <h2>Ready for the next payment run</h2>
                        <p>
                            Adjudication does not send money. It writes a claim payable. The
                            plan’s payment cycle run picks unpaid payables and, for self-funded
                            accounts, creates the funding request you see in Finance.
                        </p>
                    </header>
                    <ol className="claims-guide__path">
                        {PAY.map((step) => (
                            <li key={step.kicker}>
                                <Link to={step.to} className="provider-guide__card">
                                    <img src={step.img} alt={step.alt} />
                                    <span>{step.kicker}</span>
                                    <em>{step.when}</em>
                                    <strong>{step.name}</strong>
                                </Link>
                            </li>
                        ))}
                    </ol>
                    <p className="claims-guide__hint">
                        Needs Repair and Rejected still have no payable. A voided check must be
                        reissued before that payable is picked up again. Premium bills use a
                        billing cycle, not this path.{" "}
                        <Link to="/finance/guide">How money moves in Finance</Link>
                    </p>

                    <div id="stoploss" className="claims-guide__aside">
                        <div className="claims-guide__aside-lead">
                            <img src={homeStopLoss} alt="An umbrella over the employer" />
                            <div>
                                <p className="provider-guide__eyebrow">Stop-loss</p>
                                <h3>Same payable, different total</h3>
                                <p>
                                    After HRP writes what we owe the provider, that paid
                                    amount can also add up on the account’s reinsurance
                                    contract. Deductible and OOP already finished.
                                </p>
                            </div>
                        </div>
                        <ul className="claims-guide__pair">
                            {STOPLOSS.map((row) => (
                                <li key={row.name}>
                                    <strong>{row.name}</strong>
                                    <p>{row.detail}</p>
                                </li>
                            ))}
                        </ul>
                        <p className="claims-guide__hint">
                            A line marked Excluded from reinsurance does not add. The
                            monthly stop-loss charge on the employer bill is the premium,
                            not this total.
                        </p>
                        <p className="claims-guide__refs">
                            Stop-loss is not in the Vol. 8 step list. Paid-period tally:
                            Manager Vol. 2: Account and Broker (23.3), “About Reinsurer
                            Contracts.” Accumulators calculated from claims history:
                            Vol. 3: Membership and Enrollment, Accumulators Tab.
                        </p>
                    </div>
                </section>

                <section className="claims-guide__section">
                    <header>
                        <p className="provider-guide__eyebrow">Forms</p>
                        <h2>Types of claims</h2>
                    </header>
                    <ul className="claims-guide__types">
                        {TYPES.map((t) => {
                            const body = (
                                <>
                                    <strong>{t.name}</strong>
                                    <span>{t.form}</span>
                                    <em>{t.when}</em>
                                    <span className="claims-guide__type-blurb">{t.detail}</span>
                                </>
                            );
                            return (
                                <li key={t.name} className={t.opens ? "is-openable" : undefined}>
                                    {t.opens === "converted" ? (
                                        <button
                                            type="button"
                                            aria-expanded={convertedOpen}
                                            onClick={() => setConvertedOpen(true)}
                                        >
                                            {body}
                                        </button>
                                    ) : (
                                        body
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                    <p className="claims-guide__hint">
                        A replacement must be the same type (professional for professional).
                    </p>
                </section>
            </div>
            </div>
            <Drawer
                open={convertedOpen}
                onClose={() => setConvertedOpen(false)}
                direction="right"
                size={GUIDE_DRAWER_SIZE}
                duration={200}
                className="claims-guide__drawer"
                overlayClassName="claims-guide__drawer-overlay"
            >
                <div className="claims-guide__panel">
                    <header className="claims-guide__panel-head">
                        <div>
                            <p className="claims-guide__panel-kicker">Types of claims</p>
                            <h2>Converted claims</h2>
                        </div>
                        <button
                            type="button"
                            className="claims-guide__panel-close"
                            onClick={() => setConvertedOpen(false)}
                        >
                            Close
                        </button>
                    </header>
                    <div className="claims-guide__panel-body">
                        <div className="claims-guide__panel-guide">
                            <p>
                                A converted claim was already paid somewhere else and is
                                loaded into HRP so the member’s accumulators are right. HRP
                                does not adjudicate it as a new bill.
                            </p>
                            <section>
                                <h3>What counts as converted</h3>
                                <ul>
                                    <li>
                                        <strong>Cut-over</strong> — already-paid claims from
                                        a legacy payer at go-live.
                                    </li>
                                    <li>
                                        <strong>External paid claims</strong> — usually
                                        pharmacy, vision, or mental health, adjudicated on
                                        another system.
                                    </li>
                                    <li>
                                        <strong>Delegated encounters</strong> — a capitated
                                        group already took the risk; they send encounters,
                                        not bills for payment.
                                    </li>
                                </ul>
                            </section>
                            <section>
                                <h3>How they get in</h3>
                                <p>
                                    You do not create them from Create Professional,
                                    Institutional, or Dental in Manager. Those are new HRP
                                    claims.
                                </p>
                                <ol>
                                    <li>
                                        <strong>Converted Claims Loader</strong> — CSI
                                        (Converted Supplier Invoice) XML. Authorizations
                                        first if the lines point at them. Needs member,
                                        supplier, service lines, dates, billed / allowed /
                                        benefit amounts, and benefit tier.
                                    </li>
                                    <li>
                                        <strong>SOAP</strong> —{" "}
                                        <code>processConvertedSupplierInvoice</code> on
                                        ClaimServiceStronglyTypedType. Same CSI payload.
                                        Does not support Void.
                                    </li>
                                    <li>
                                        <strong>837 inbound, marked paid</strong> — BHT06 =
                                        RP goes to the CSI loader. BHT06 = CH is a normal
                                        unadjudicated claim.
                                    </li>
                                </ol>
                            </section>
                            <section>
                                <h3>In Manager after it exists</h3>
                                <ol>
                                    <li>
                                        Claim Search →{" "}
                                        <strong>Converted Claim = Yes</strong>
                                    </li>
                                    <li>
                                        View — the <strong>Converted</strong> field is
                                        checked
                                    </li>
                                    <li>Adjust if needed</li>
                                    <li>
                                        <strong>Renew Claim</strong> if it must behave like
                                        a normal HRP claim. Same claim number, next version.
                                        After renew, HRP assumes the line paid amount is
                                        already paid and writes extra payables or
                                        receivables — even if the converted claim was
                                        Denied.
                                    </li>
                                </ol>
                            </section>
                            <p>
                                You can adjust a converted claim, but you cannot adjudicate
                                or reprocess it until it is renewed. You cannot replace or
                                void it the way you do a normal claim. After a successful
                                renew, Converted = Yes will not find it — the renewed
                                version replaced it.
                            </p>
                        </div>
                    </div>
                </div>
            </Drawer>
        </div>
    );
};

export default ClaimsGuide;
